import { useState } from 'react';
import { NavLink, Outlet, useOutletContext, useParams } from 'react-router-dom';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { CompanyLogo } from '@/components/CompanyLogo';
import { ErrorState } from '@/components/ui/States';
import { Skeleton, SkeletonRows } from '@/components/ui/Skeleton';
import { StatusPill } from '@/components/ui/StatusPill';
import { toast } from '@/components/ui/Toast';
import { cn } from '@/lib/cn';
import { useCompanyRole } from '@/app/store';
import { useAsync } from '@/lib/useAsync';
import { api, ApiError, type CompanyDetail } from '@/lib/api';

export interface CompanyCtx {
  company: CompanyDetail;
  reloadCompany: () => void;
}

export const useCompanyCtx = () => useOutletContext<CompanyCtx>();

/** Editorial section header shared by the nested layouts — display title over
 * the short report-style gradient rule. */
export function LayoutHeader({ title, description }: { title: string; description?: string | null }) {
  return (
    <div className="mb-5 mt-1">
      <h1 className="text-[26px] font-semibold leading-tight">{title}</h1>
      <div className="grad-bar mt-3 h-[3px] w-14 rounded-full" aria-hidden />
      {description && <p className="mt-3 max-w-xl text-sm text-fg-muted">{description}</p>}
    </div>
  );
}

/** Small pill-style tab bar shared by the company/workspace/project layouts. */
export function TabBar({ tabs }: { tabs: { to: string; label: string; end?: boolean }[] }) {
  return (
    <nav className="mb-6 flex flex-wrap gap-1 border-b border-border">
      {tabs.map((t) => (
        <NavLink
          key={t.to}
          to={t.to}
          end={t.end}
          className={({ isActive }) =>
            cn(
              '-mb-px border-b-2 px-3 py-2 text-sm font-medium transition',
              isActive
                ? 'border-primary text-primary'
                : 'border-transparent text-fg-muted hover:border-border-strong hover:text-fg',
            )
          }
        >
          {t.label}
        </NavLink>
      ))}
    </nav>
  );
}

/** Resolves /c/:companySlug → company detail (slug → id via the companies
 * list), then provides it to child routes via outlet context. */
export function CompanyLayout() {
  const { companySlug, workspaceSlug } = useParams<{ companySlug: string; workspaceSlug?: string }>();
  const [statusBusy, setStatusBusy] = useState(false);

  const company = useAsync(async () => {
    const all = await api.listCompanies();
    const match = all.find((c) => c.slug === companySlug);
    if (!match) throw new ApiError('Company not found.', 404, 'not_found');
    return api.getCompany(match.id);
  }, [companySlug]);

  // Tab visibility mirrors the API: People = any member, Knowledge = staff +
  // company ADMIN, Settings = staff.
  const role = useCompanyRole(company.status === 'success' ? company.data.id : undefined);
  const isStaff = role === 'STAFF';

  if (company.status === 'loading') {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <SkeletonRows rows={3} />
      </div>
    );
  }
  if (company.status === 'error') {
    return <ErrorState error={company.error} onRetry={company.reload} />;
  }

  const c = company.data;
  const ctx = { company: c, reloadCompany: company.reload } satisfies CompanyCtx;

  async function setCompanyStatus(status: string) {
    setStatusBusy(true);
    try {
      await api.updateCompany(c.id, { status: status as 'active' | 'paused' | 'archived' });
      company.reload();
    } catch (err) {
      toast.danger('Could not change status', err instanceof ApiError ? err.message : undefined);
    } finally {
      setStatusBusy(false);
    }
  }

  // Only the deepest active level draws chrome — when a workspace (or deeper)
  // route is active, this layout is data-plumbing only. Three stacked
  // breadcrumb+title+tab blocks was the alternative.
  if (workspaceSlug) return <Outlet context={ctx} />;

  return (
    <>
      <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: c.name }]} />
      <div className="flex items-start gap-4">
        <CompanyLogo name={c.name} websiteUrl={c.websiteUrl} logoUrl={c.logoUrl} size={48} className="mt-1.5" />
        <LayoutHeader title={c.name} />
        <span className="mt-3">
          <StatusPill
            status={c.status}
            busy={statusBusy}
            onChange={isStaff ? (s) => void setCompanyStatus(s) : undefined}
          />
        </span>
      </div>
      <TabBar
        tabs={[
          { to: `/c/${c.slug}`, label: 'Workspaces', end: true },
          { to: `/c/${c.slug}/activity`, label: 'Activity' },
          { to: `/c/${c.slug}/deliverables`, label: 'Deliverables' },
          { to: `/c/${c.slug}/people`, label: 'People' },
          ...(isStaff || role === 'ADMIN' ? [{ to: `/c/${c.slug}/knowledge`, label: 'Knowledge' }] : []),
          ...(isStaff ? [{ to: `/c/${c.slug}/settings`, label: 'Settings' }] : []),
        ]}
      />
      <Outlet context={ctx} />
    </>
  );
}
